import { useEffect } from "react";
import { OG_LOCALE, localizePath, splitLocalePath } from "@/i18n/config";
import { useLocale } from "@/i18n/useLocale";

export const SITE_URL = "https://www.thebyteline.com";
export const SITE_NAME = "Byteline";

export interface SeoProps {
  title: string;
  description: string;
  /** Path only, e.g. "/feed" or "/article/some-slug". Defaults to the current path. */
  path?: string;
  image?: string | null;
  type?: "website" | "article";
  noIndex?: boolean;
  publishedTime?: string | null;
  modifiedTime?: string | null;
  author?: string | null;
  section?: string | null;
  /** Extra JSON-LD blocks. */
  jsonLd?: Record<string, unknown>[];
}

const MANAGED = "data-byteline-seo";

const truncate = (value: string, max: number) => {
  const clean = (value || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trimEnd()}…`;
};

const upsertMeta = (attr: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MANAGED, "");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertCanonical = (href: string) => {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute(MANAGED, "");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export const Seo = ({
  title,
  description,
  path,
  image,
  type = "website",
  noIndex = false,
  publishedTime,
  modifiedTime,
  author,
  section,
  jsonLd = [],
}: SeoProps) => {
  const locale = useLocale();
  const metaTitle = truncate(title, 60);
  const metaDescription = truncate(description, 158);
  const shareImage = image || `${SITE_URL}/icon.png`;
  const jsonLdKey = JSON.stringify(jsonLd);

  useEffect(() => {
    if (typeof document === "undefined") return;

    // Browser paths already carry the language prefix; strip it before
    // re-applying so the canonical never doubles up.
    const rawPath = splitLocalePath(path ?? window.location.pathname).pathname;
    const pathname = localizePath(rawPath, locale);
    const canonical = `${SITE_URL}${pathname.startsWith("/") ? pathname : `/${pathname}`}`;

    document.title = metaTitle;
    upsertMeta("name", "description", metaDescription);
    upsertCanonical(canonical);
    upsertMeta(
      "name",
      "robots",
      noIndex
        ? "noindex, nofollow"
        : "index, follow, max-image-preview:large, max-snippet:-1",
    );

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", metaTitle);
    upsertMeta("property", "og:description", metaDescription);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:locale", OG_LOCALE[locale]);
    upsertMeta("property", "og:image", shareImage);
    upsertMeta("property", "og:image:width", "1200");
    upsertMeta("property", "og:image:height", "630");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", metaTitle);
    upsertMeta("name", "twitter:description", metaDescription);
    upsertMeta("name", "twitter:image", shareImage);

    if (type === "article") {
      if (publishedTime) upsertMeta("property", "article:published_time", publishedTime);
      if (modifiedTime) upsertMeta("property", "article:modified_time", modifiedTime);
      if (author) upsertMeta("property", "article:author", author);
      if (section) upsertMeta("property", "article:section", section);
    }

    // The router re-renders the document head after hydration/navigation, which
    // can reset the title. Re-apply it once after paint so ours wins.
    const raf = requestAnimationFrame(() => {
      document.title = metaTitle;
      upsertMeta("name", "description", metaDescription);
      upsertCanonical(canonical);
    });

    const blocks: Record<string, unknown>[] = JSON.parse(jsonLdKey);
    const scripts = blocks.map((block) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.setAttribute(MANAGED, "");
      el.textContent = JSON.stringify(block);
      document.head.appendChild(el);
      return el;
    });

    return () => {
      cancelAnimationFrame(raf);
      scripts.forEach((el) => el.remove());
    };
  }, [
    locale,
    metaTitle,
    metaDescription,
    path,
    shareImage,
    type,
    noIndex,
    publishedTime,
    modifiedTime,
    author,
    section,
    jsonLdKey,
  ]);

  return null;
};

export default Seo;
